import { debounce } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import { useAppStore } from '@/hooks'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'
import { findSystemNodeId } from '@/store/modules/node/helpers'
import { NamingTypeEnum } from '@/types'
import { NativeMap } from '@/utils'

import { getCanGenerateNaming } from './getCanGenerateNaming'

interface GenerateNamingState {
	generateColumnCode: boolean
	generateElementCode: boolean
}

/** Check parent folders & system if the node can auto generate naming */
export const useCanGenerateNaming = (
	node: StructureDto,
	namingType?: NamingTypeEnum,
	isThisFolder?: boolean,
) => {
	const nodes = useAppStore((state) => state.node.nodes)
	const systemId = findSystemNodeId(node, nodes as NativeMap<StructureDto>)
	const systemsSlice = useAppStore((state) => state.system.systems)
	const foldersSlice = useAppStore((state) => state.folder.folders)

	const letterCaseToggle = systemsSlice[systemId]?.form?.letterCaseToggle

	const isUpperCase =
		letterCaseToggle === 'true' || letterCaseToggle === undefined

	const [namingState, setNamingState] = useState<GenerateNamingState>({
		generateElementCode: false,
		generateColumnCode: false,
	})

	// Get parent node to track its generation settings
	const parentId = nodes[node?.id]?.parentStructureId
	const parentFolderGenerateCode = parentId ? foldersSlice[parentId]?.form?.generateCode : undefined
	const parentFolderGenerateColumnCode = parentId ? foldersSlice[parentId]?.form?.generateTableColumnCode : undefined
	const parentSystemGenerateCode = parentId ? systemsSlice[parentId]?.form?.generateCode : undefined
	const parentSystemGenerateColumnCode = parentId ? systemsSlice[parentId]?.form?.generateTableColumnCode : undefined

	// Also track root system settings (inheritance fallback)
	const systemGenerateCode = systemsSlice[systemId]?.form?.generateCode
	const systemGenerateColumnCode = systemsSlice[systemId]?.form?.generateTableColumnCode

	const getCanGenerateNamingDebounced = useMemo(
		() =>
			debounce(async () => {
				if (node?.id) {
					const result = await getCanGenerateNaming(
						node?.id,
						namingType,
						isThisFolder,
					)

					setNamingState(result)
				}
			}, 200),

		[isThisFolder, namingType, node?.id, parentFolderGenerateCode, parentFolderGenerateColumnCode, parentSystemGenerateCode, parentSystemGenerateColumnCode, systemGenerateCode, systemGenerateColumnCode],
	)

	useEffect(() => {
		getCanGenerateNamingDebounced()
	}, [getCanGenerateNamingDebounced])

	return {
		generateElementCode: namingState.generateElementCode || false,
		generateColumnCode: namingState.generateColumnCode || false,
		isUpperCase,
	}
}
